
import { VezgoConnection, VezgoError } from '../types';
import { ConnectionStatus, ConnectionHealth } from '@/providers/common/types';
import { logger } from '@/services/LoggingService';
import { supabase } from '@/integrations/supabase/client';
import { getUser } from '@/services/UserService';

class ConnectionService {
  private connections: Map<string, VezgoConnection> = new Map();
  private initialized: boolean = false;
  private refreshInProgress: boolean = false;
  
  /**
   * Initialize the connection service
   */
  public async initialize(): Promise<void> {
    try {
      this.connections.clear();
      
      // Load existing connections from the database
      const user = await getUser();
      if (user) {
        logger.info('ConnectionService', 'Loading connections from database for user', { 
          userId: user.id,
          timestamp: new Date().toISOString() 
        });
        
        const { data, error } = await supabase
          .from('vezgo_connections')
          .select('*')
          .eq('user_id', user.id);
        
        if (error) {
          logger.error('ConnectionService', 'Error loading connections', {
            error: error.message,
            code: error.code,
            details: error.details,
            timestamp: new Date().toISOString()
          });
          throw error;
        }
        
        if (data && data.length > 0) {
          logger.info('ConnectionService', 'Found existing connections', { 
            count: data.length,
            timestamp: new Date().toISOString() 
          });
          
          data.forEach(conn => {
            // Use vezgo_account_id as the primary identifier
            const connectionIdentifier = conn.vezgo_account_id;
            const lastCheckedTime = conn.last_sync_at || conn.updated_at;
            const lastChecked = lastCheckedTime ? new Date(lastCheckedTime) : new Date();
            
            const metadata: Record<string, any> = {};
            if (conn.metadata && typeof conn.metadata === 'object' && !Array.isArray(conn.metadata)) {
              Object.assign(metadata, conn.metadata);
            }
            
            const vezgoConnection: VezgoConnection = {
              id: conn.id,
              provider: connectionIdentifier,
              provider_name: conn.provider_name,
              user_id: user.id,
              created_at: conn.created_at,
              updated_at: conn.updated_at,
              connection_status: conn.connection_status as ConnectionStatus,
              status: conn.connection_status as ConnectionStatus,
              lastChecked: lastChecked,
              metadata: metadata
            };
            
            this.connections.set(connectionIdentifier, vezgoConnection);
            logger.debug('ConnectionService', 'Loaded connection', { 
              provider: connectionIdentifier, 
              id: conn.id,
              timestamp: new Date().toISOString() 
            });
          });
        } else {
          logger.info('ConnectionService', 'No existing connections found', {
            userId: user.id,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        logger.warn('ConnectionService', 'No authenticated user, skipping connection loading', {
          timestamp: new Date().toISOString()
        });
      }
      
      this.initialized = true;
      logger.info('ConnectionService', 'Initialized', { 
        connectionsLoaded: this.connections.size,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('ConnectionService', 'Error initializing', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }
  
  /**
   * Check if the service is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }
  
  /**
   * Add or update a connection
   * 
   * @param provider - Provider name
   * @param connection - Connection object
   */
  public setConnection(provider: string, connection: VezgoConnection): void {
    this.connections.set(provider, connection);
    
    logger.debug('ConnectionService', 'Set connection', {
      provider,
      connection
    });
  }
  
  /**
   * Get a connection by provider
   * 
   * @param provider - Provider name
   * @returns Connection object or null if not found
   */
  public getConnection(provider: string): VezgoConnection | null {
    return this.connections.get(provider) || null;
  }
  
  /**
   * Remove a connection
   * 
   * @param provider - Provider name
   * @returns True if connection was removed
   */
  public removeConnection(provider: string): boolean {
    const result = this.connections.delete(provider);
    
    logger.debug('ConnectionService', 'Remove connection', {
      provider,
      success: result
    });
    
    return result;
  }
  
  /**
   * Check if a connection exists
   * 
   * @param provider - Provider name
   * @returns True if connection exists
   */
  public hasConnection(provider: string): boolean {
    return this.connections.has(provider);
  }
  
  /**
   * Get all connections
   * 
   * @returns Map of all connections
   */
  public getAllConnections(): Map<string, VezgoConnection> {
    return this.connections;
  }
  
  /**
   * Get connection count
   * 
   * @returns Number of connections
   */
  public getConnectionCount(): number {
    return this.connections.size;
  }
  
  /**
   * Generate a secure random state string for CSRF protection
   * 
   * @returns Random state string
   */
  public generateState(): string {
    return crypto.randomUUID();
  }
  
  /**
   * Set the refresh state
   * 
   * @param isRefreshing - Whether a refresh is in progress
   */
  public setRefreshState(isRefreshing: boolean): void {
    this.refreshInProgress = isRefreshing;
  }
  
  /**
   * Check if a refresh is in progress
   * 
   * @returns True if refresh is in progress
   */
  public isRefreshInProgress(): boolean {
    return this.refreshInProgress;
  }
  
  /**
   * Get connection health information
   * 
   * @param provider - Provider name
   * @returns Connection health information or null if not found
   */
  public getConnectionHealth(provider: string): ConnectionHealth | null {
    const connection = this.getConnection(provider);
    
    if (!connection) {
      return null;
    }
    
    return {
      status: connection.status === 'connected' ? 'healthy' : 'error',
      lastCheck: connection.lastChecked ? new Date(connection.lastChecked) : undefined,
      errorCode: connection.errorCode,
      errorMessage: connection.errorMessage,
      issues: connection.errorMessage ? [connection.errorMessage] : []
    };
  }
  
  /**
   * Delete a connection and clean up associated data
   * 
   * @param provider - Provider name
   * @returns True if connection was deleted
   */
  public async deleteConnection(provider: string): Promise<boolean> {
    const startTime = Date.now();
    try {
      const connection = this.getConnection(provider);
      
      if (!connection) {
        logger.warn('ConnectionService', 'Cannot delete non-existent connection', {
          provider,
          timestamp: new Date().toISOString()
        });
        return false;
      }
      
      // Get the connection record from the database to get the ID
      const user = await getUser();
      if (!user) {
        logger.warn('ConnectionService', 'No authenticated user for connection deletion', {
          provider,
          timestamp: new Date().toISOString()
        });
        return false;
      }
      
      const { data: connData, error: connError } = await supabase
        .from('vezgo_connections')
        .select('id')
        .eq('user_id', user.id)
        .eq('vezgo_account_id', provider) // Fixed: use vezgo_account_id instead of provider
        .single();
      
      if (connError || !connData) {
        logger.error('ConnectionService', 'Error retrieving connection record', {
          error: connError?.message || 'Connection record not found',
          provider,
          userId: user.id,
          timestamp: new Date().toISOString()
        });
      } else {
        logger.info('ConnectionService', 'Deleting account data for connection', {
          connectionId: connData.id,
          provider,
          userId: user.id,
          timestamp: new Date().toISOString()
        });
        
        // Call the delete_vezgo_accounts function to clean up accounts and holdings
        const { error: deleteError } = await supabase.rpc(
          'delete_vezgo_accounts',
          {
            user_id_param: user.id,
            institution_id_param: null,
            connection_id_param: connData.id
          }
        );
        
        if (deleteError) {
          logger.error('ConnectionService', 'Error deleting accounts and holdings', {
            error: deleteError.message,
            provider,
            userId: user.id,
            timestamp: new Date().toISOString()
          });
        } else {
          logger.info('ConnectionService', 'Successfully deleted accounts and holdings', {
            connectionId: connData.id,
            provider,
            userId: user.id,
            timestamp: new Date().toISOString()
          });
        }
        
        // Delete the connection record from the database
        const { error: deleteConnError } = await supabase
          .from('vezgo_connections')
          .delete()
          .eq('id', connData.id);
        
        if (deleteConnError) {
          logger.error('ConnectionService', 'Error deleting connection record', {
            error: deleteConnError.message,
            connectionId: connData.id,
            provider,
            timestamp: new Date().toISOString()
          });
        } else {
          logger.info('ConnectionService', 'Successfully deleted connection record', {
            connectionId: connData.id,
            provider,
            timestamp: new Date().toISOString()
          });
        }
      }
      
      // Remove the connection from local storage
      const result = this.removeConnection(provider);
      
      logger.info('ConnectionService', 'Deleted connection', {
        provider,
        success: result,
        duration: `${Date.now() - startTime}ms`,
        timestamp: new Date().toISOString()
      });
      
      return result;
    } catch (error) {
      logger.error('ConnectionService', 'Error deleting connection', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        provider,
        duration: `${Date.now() - startTime}ms`,
        timestamp: new Date().toISOString()
      });
      return false;
    }
  }
  
  /**
   * Update connection status
   * 
   * @param provider - Provider name
   * @param status - New status
   * @param error - Optional error details
   */
  public async updateConnectionStatus(
    provider: string,
    status: ConnectionStatus,
    error?: VezgoError
  ): Promise<boolean> {
    try {
      const connection = this.getConnection(provider);
      
      if (!connection) {
        logger.warn('ConnectionService', 'Cannot update status for non-existent connection', {
          provider
        });
        return false;
      }
      
      // Update the connection
      connection.status = status;
      
      if (error) {
        connection.errorCode = error.code;
        connection.errorMessage = error.message;
      } else {
        connection.errorCode = undefined;
        connection.errorMessage = undefined;
      }
      
      this.setConnection(provider, connection);
      
      logger.debug('ConnectionService', 'Updated connection status', {
        provider,
        status,
        hasError: !!error
      });
      
      return true;
    } catch (error) {
      logger.error('ConnectionService', 'Error updating connection status', {
        error: error instanceof Error ? error.message : String(error),
        provider,
        status
      });
      return false;
    }
  }
}

// Export a singleton instance
export const connectionService = new ConnectionService();
